import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Typography,
    Box,
    Stack,
    CircularProgress,
} from '@mui/material';
import { Sync as SyncIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import { useBolSync, useBolSyncJobStatus } from '../hooks/useBolConnector';
import { Connection } from '@domain/connection/models/Connection';
import { BolEndpoint } from '@domain/connection/enums/endpoints.enum';
import { useToast } from '@shared/hooks/useToast';
import { useImportJobsStore } from '../../../../application/store/importJobsStore';
import { estimateRemaining } from '@shared/utils/progressEta';

interface BolSyncDialogProps {
    open: boolean;
    marketplaceConnection: Connection;
    onClose: () => void;
}

const syncOptions = [
    { id: BolEndpoint.ORDERS_ALL, label: 'All Orders' },
    { id: BolEndpoint.ORDERS_NEW, label: 'New Orders' },
    { id: BolEndpoint.RANKS, label: 'Product Ranks' },
    { id: BolEndpoint.SEARCH_TERMS, label: 'Search Terms' },
    { id: BolEndpoint.INVENTORY, label: 'Inventory' },
    { id: BolEndpoint.OFFERS, label: 'Offers' },
    { id: BolEndpoint.INVOICES, label: 'Invoice Specification' },
];

interface SyncOptionButtonProps {
    label: string;
    jobId: string | null;
    startedAt: number | null;
    onClick: () => void;
}

const SyncOptionButton: React.FC<SyncOptionButtonProps> = ({ label, jobId, startedAt, onClick }) => {
    const { data: jobStatus } = useBolSyncJobStatus(jobId);
    const isRunning = !!jobId && jobStatus?.state !== 'completed' && jobStatus?.state !== 'failed' && jobStatus?.state !== 'not_found';
    const isFailed = jobStatus?.state === 'failed' || jobStatus?.state === 'not_found';
    const isCompleted = jobStatus?.state === 'completed';
    const progress = jobStatus?.progress ?? 0;
    const eta = isRunning && startedAt ? estimateRemaining(startedAt, progress) : null;

    let icon = <SyncIcon />;
    if (isRunning) {
        icon = progress > 0
            ? <CircularProgress size={18} variant="determinate" value={progress} />
            : <CircularProgress size={18} />;
    }
    else if (isFailed) icon = <ErrorIcon color="error" />;
    else if (isCompleted) icon = <CheckCircleIcon color="success" />;

    return (
        <Button
            fullWidth
            variant="outlined"
            startIcon={icon}
            onClick={onClick}
            disabled={isRunning}
            sx={{
                height: '48px',
                justifyContent: 'flex-start',
                textTransform: 'none',
            }}
        >
            <Stack alignItems="flex-start" sx={{ minWidth: 0, overflow: 'hidden' }}>
                <Typography variant="body2" noWrap>{label}</Typography>
                {eta && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                        {eta}
                    </Typography>
                )}
            </Stack>
        </Button>
    );
};

export const BolSyncDialog: React.FC<BolSyncDialogProps> = ({ open, onClose, marketplaceConnection }) => {

    const { sync } = useBolSync();
    const { error } = useToast();
    const addJob = useImportJobsStore((s) => s.addJob);

    const [jobIds, setJobIds] = useState<Record<string, string | null>>({});
    const [jobStartTimes, setJobStartTimes] = useState<Record<string, number>>({});

    const handleSyncClick = async (endpoint: BolEndpoint) => {
        try {
            const { jobId } = await sync({ connectionId: marketplaceConnection.id, endpoint });
            const startedAt = Date.now();
            setJobIds((prev) => ({ ...prev, [endpoint]: jobId }));
            setJobStartTimes((prev) => ({ ...prev, [endpoint]: startedAt }));
            const label = syncOptions.find((option) => option.id === endpoint)?.label ?? endpoint;
            addJob({ id: jobId, connector: 'bol', label: `${marketplaceConnection.name} · ${label}`, startedAt });
        } catch (e) {
            error(e instanceof Error ? e.message : 'Failed to start sync');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <SyncIcon />
                    <Typography variant="h6">Sync Data</Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    {syncOptions.map((option) => (
                        <Grid item xs={6} key={option.id}>
                            <SyncOptionButton
                                label={option.label}
                                jobId={jobIds[option.id] ?? null}
                                startedAt={jobStartTimes[option.id] ?? null}
                                onClick={() => handleSyncClick(option.id)}
                            />
                        </Grid>
                    ))}
                </Grid>

            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
